<template>
  <div :class="$style.wrapper" @keydown="onKeyDown">
    <input ref="inputRef" :value="query" type="search" autocomplete="off" v-bind="attrs" @input="onInput" @focus="onFocus" @blur="onBlur" @compositionstart="onCompositionStart" @compositionend="onCompositionEnd" />
    <ul v-if="isOpen && allItems.length > 0" :class="$style.list">
      <li v-for="(item, index) in allItems" :key="item.keyword" :class="[$style.item, index === activeIndex && $style.active]" @mousedown.prevent="select(item.keyword)" @mouseover="activeIndex = index">
        <slot v-if="!item.isRefinement" name="completion-item" :suggestion="item">{{ item.keyword }}</slot>
        <slot v-else name="refinement-item" :suggestion="item">{{ item.keyword }}</slot>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useAttrs, onBeforeUnmount } from 'vue';
import { get as getSuggestions } from '@/js/lib/api/v2/public/search/suggestions';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    debounce?: number;
    minCompletions?: number;
    minLength?: number;
    total?: number;
  }>(),
  {
    debounce: 300,
    minCompletions: 2,
    minLength: 1,
    total: 5,
  },
);
const attrs = useAttrs();
const query = defineModel<string>({ default: '' });
const inputRef = ref<HTMLInputElement>();
const completions = ref<{ keyword: string }[]>([]);
const refinements = ref<{ keyword: string }[]>([]);
const isOpen = ref(false);
const activeIndex = ref(-1);
const isComposing = ref(false);
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let abortController: AbortController | undefined;

const allItems = computed(() => {
  const refinementCount = Math.min(refinements.value.length, props.total - props.minCompletions);
  const completionCount = Math.min(completions.value.length, props.total - refinementCount);
  return [
    ...completions.value.slice(0, completionCount).map((item) => {
      return { ...item, isRefinement: false };
    }),
    ...refinements.value.slice(0, refinementCount).map((item) => {
      return { keyword: `${query.value} ${item.keyword}`, isRefinement: true };
    }),
  ];
});

const fetchSuggestions = async (q: string) => {
  if (q.length < props.minLength) {
    completions.value = [];
    refinements.value = [];
    isOpen.value = false;
    return;
  }
  abortController?.abort();
  abortController = new AbortController();
  try {
    const res = await getSuggestions({ q }, abortController.signal);
    completions.value = res.data.completions;
    refinements.value = res.data.refinements;
    isOpen.value = allItems.value.length > 0;
    activeIndex.value = -1;
    if (isOpen.value) {
      window.dataLayer?.push({ event: 'suggest_shown', search_term: q, search_term_length: q.length, suggest_count: allItems.value.length });
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    completions.value = [];
    refinements.value = [];
    isOpen.value = false;
  }
};

const scheduleFetch = (q: string) => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => fetchSuggestions(q), props.debounce);
};

const onCompositionStart = () => {
  isComposing.value = true;
};

const onCompositionEnd = (e: Event) => {
  isComposing.value = false;
  // Safari では input → compositionend の順で発火するため、
  // compositionend で明示的に fetch を呼ぶ必要がある
  const value = (e.target as HTMLInputElement).value;
  query.value = value;
  scheduleFetch(value);
};

const onInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  query.value = value;
  if (!isComposing.value) {
    scheduleFetch(value);
  }
};

const onFocus = () => {
  scheduleFetch(query.value);
  if (allItems.value.length > 0) {
    isOpen.value = true;
  }
};

const onBlur = () => {
  isOpen.value = false;
};

const select = (keyword: string) => {
  window.dataLayer?.push({ event: 'suggest_clicked', search_term: query.value, search_term_length: query.value.length, suggest_selected: keyword });
  query.value = keyword;
  isOpen.value = false;
  if (inputRef.value) {
    inputRef.value.value = keyword;
    inputRef.value.closest('form')?.submit();
  }
};

const onKeyDown = (e: KeyboardEvent) => {
  if (!isOpen.value) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIndex.value = Math.min(activeIndex.value + 1, allItems.value.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, -1);
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault();
    select(allItems.value[activeIndex.value].keyword);
  } else if (e.key === 'Escape') {
    isOpen.value = false;
    activeIndex.value = -1;
  }
};

defineExpose({
  focus: () => inputRef.value?.focus(),
});

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
  abortController?.abort();
});

watch(query, (value) => {
  if (value.length < props.minLength) {
    completions.value = [];
    refinements.value = [];
    isOpen.value = false;
  }
});
</script>

<style module lang="scss">
.wrapper {
  position: relative;
}

.list {
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgb(0 0 0 / 12%);
  left: 0;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 10;
}

.item {
  cursor: pointer;
  overflow: hidden;
  padding: 6px 10px;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (--sp) {
    font-size: 16px;
  }

  &:hover,
  &.active {
    background-color: var(--color-gray150);
  }
}
</style>
